import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { DayForecast, WeatherForecastResponse } from './models';
import { BaseService } from '../services/base.service';

@Injectable({
  providedIn: 'root'
})
export class WeatherForecastService extends BaseService {
  private weatherApiUrl = this.API_ADMIN_BASE_URL + 'v1/polaris-weather/api/';

  constructor(private http: HttpClient) {
    super();
  }

  getWeatherForecast(lat: number, lng: number): Observable<DayForecast[]> {
    const endDate = this.getEndDate();
    const params = ['maxTemperature', 'minTemperature', 'precipitation', 'precipitationProbability', 'windSpeed', 'windDirection', 'referenceEvapotranspiration'];
    const parametersQuery = params.map(p => `parameters=${p}`).join('&');
    const url = `${this.weatherApiUrl}forecast?location=${lat},${lng}&aggregation=daily&endDate=${endDate}&${parametersQuery}`;

    return this.http.get<WeatherForecastResponse>(url).pipe(
      map(response => this.mapResponseToForecasts(response))
    );
  }

  private getEndDate(): string {
    const date = new Date();
    date.setDate(date.getDate() + 3);
    return date.toISOString().split('T')[0];
  }

  private mapResponseToForecasts(response: WeatherForecastResponse): DayForecast[] {
    const params = response.data.parameters;
    return response.dates.slice(0, 3).map((dateStr, i) => {
      const date = new Date(dateStr);
      const dayName = i === 0 ? 'Today' : i === 1 ? 'Tomorrow' : date.toLocaleDateString('en-US', { weekday: 'long' });
      const precipProb = params.precipitationProbability?.[i] ?? 0;
      const precip = params.precipitation?.[i] ?? 0;
      const windSpeed = params.windSpeed?.[i] ?? 0;
      const maxTemp = params.maxTemperature?.[i] ?? 0;
      const minTemp = params.minTemperature?.[i] ?? 0;
      return {
        dayName,
        date: date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
        maxTemp: `${Math.round(maxTemp)}°C`,
        minTemp: `${Math.round(minTemp)}°C`,
        et: `${(params.referenceEvapotranspiration?.[i] ?? 0).toFixed(1)} mm`,
        wind: `${Math.round(windSpeed)} m/s`,
        windDir: this.degreesToDirection(params.windDirection?.[i] ?? 0),
        rain: `${precip.toFixed(1)} mm`,
        icon: this.getWeatherIcon(precipProb, precip, windSpeed, maxTemp, minTemp)
      };
    });
  }

  private getWeatherIcon(precipProb: number, precip: number, windSpeed: number, maxTemp: number, minTemp: number): string {
    if (precipProb > 50 && maxTemp < 2) return 'snowing_weather_forecast_icon.png';
    if (precip > 10 && precipProb > 70) return 'thunder_weather_forecast_icon.png';
    if (precipProb > 60) return 'raining_weather_forecast_icon.png';
    if (windSpeed > 40) return 'windy_weather_forecast_icon.png';
    if (maxTemp > 35) return 'heat_wave_weather_forecast_icon.png';
    if (maxTemp < 10) return 'cold_weather_forecast_icon.png';
    if (precipProb >= 30 && precipProb <= 60) return 'cloudy_weather_forecast_icon.png';
    if (precipProb >= 10 && precipProb < 30) return 'partly_cloudy_weather_forecast_icon.png';
    return 'clear_weather_forecast_icon.png';
  }

  private degreesToDirection(degrees: number): string {
    const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
    return directions[Math.floor(((degrees + 22) / 45) % 8)];
  }
}
